import React, { useEffect, useRef, useState, useMemo } from 'react'
import * as THREE from 'three'
import ThreeGlobe from 'three-globe'
import { useTheme } from '../contexts/ThemeContext'

const ACCENT = '#428284'
const ACCENT_LIGHT = '#75cbcb'
const ACCENT_BRIGHT = '#5fd6c8'

// African cities highlighted as pulsing rings
const AFRICA_RINGS = [
  { lat: 13.5128, lng: 2.1128, label: 'Niamey' },        // Zarma heartland
  { lat: 12.6392, lng: -8.0029, label: 'Bamako' },       // Bambara
  { lat: 14.6928, lng: -17.4467, label: 'Dakar' },       // Pulaar/Wolof
  { lat: 11.8500, lng: 13.1600, label: 'Maiduguri' },    // Kanuri (Beri-Beri)
  { lat: 9.0579, lng: 7.4951, label: 'Abuja' },          // Hausa/Nigeria
  { lat: 6.5244, lng: 3.3792, label: 'Lagos' },          // Yoruba
  { lat: 5.6037, lng: -0.1870, label: 'Accra' },         // Akan
  { lat: 12.3714, lng: -1.5197, label: 'Ouagadougou' },  // Mooré
  { lat: 9.0320, lng: 38.7469, label: 'Addis Ababa' },   // Amharic
  { lat: -1.2921, lng: 36.8219, label: 'Nairobi' },      // Swahili
]

const AFRICA_LANG_ARCS = [
  { startLat: 13.5128, startLng: 2.1128, endLat: 14.6928, endLng: -17.4467 },  // Niamey-Dakar
  { startLat: 13.5128, startLng: 2.1128, endLat: 12.6392, endLng: -8.0029 },   // Niamey-Bamako
  { startLat: 13.5128, startLng: 2.1128, endLat: 11.8500, endLng: 13.1600 },   // Niamey-Maiduguri
  { startLat: 13.5128, startLng: 2.1128, endLat: 9.0579, endLng: 7.4951 },     // Niamey-Abuja
  { startLat: 12.3714, startLng: -1.5197, endLat: 13.5128, endLng: 2.1128 },   // Ouaga-Niamey
  { startLat: 9.0320, startLng: 38.7469, endLat: 13.5128, endLng: 2.1128 },    // Addis-Niamey
]

interface CountryFeature {
  properties: {
    CONTINENT?: string
    ADMIN?: string
    NAME?: string
  }
  geometry: unknown
}

const GlobeShowcase: React.FC = () => {
  const mountRef = useRef<HTMLDivElement>(null)
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  const [ready, setReady] = useState(false)
  const [supported, setSupported] = useState(true)

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }, [])

  // Refs to keep state across re-renders without re-creating the globe
  const themeRef = useRef(isDark)
  themeRef.current = isDark

  useEffect(() => {
    if (!mountRef.current) return

    // WebGL check
    try {
      const canvas = document.createElement('canvas')
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
      if (!gl) {
        setSupported(false)
        return
      }
    } catch {
      setSupported(false)
      return
    }

    const mount = mountRef.current
    const width = mount.clientWidth
    const height = mount.clientHeight

    const scene = new THREE.Scene()

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 2000)
    camera.position.z = 320

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true })
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    renderer.setSize(width, height)
    renderer.setClearColor(0x000000, 0)
    mount.appendChild(renderer.domElement)

    // Lighting — keep flat-ish, dots read better with even light
    scene.add(new THREE.AmbientLight(0xffffff, 1.1))
    const dir = new THREE.DirectionalLight(0xffffff, 0.4)
    dir.position.set(1, 1, 1)
    scene.add(dir)

    const globe = new ThreeGlobe({ waitForGlobeReady: true, animateIn: false })
      .showAtmosphere(true)
      .atmosphereAltitude(0.18)
      .atmosphereColor(ACCENT_LIGHT)

    // Make the underlying sphere transparent so only dots show (Cloudflare look)
    const sphereMat = globe.globeMaterial() as THREE.MeshPhongMaterial
    sphereMat.transparent = true
    sphereMat.opacity = 0
    sphereMat.depthWrite = false

    scene.add(globe)

    // Tilt + rotate so Africa faces camera initially
    globe.rotation.y = -Math.PI / 12   // small westward turn
    globe.rotation.x = Math.PI / 12    // slight downward tilt to show Africa center

    // Pulsing rings on African capitals
    globe
      .ringsData(AFRICA_RINGS)
      .ringLat('lat')
      .ringLng('lng')
      .ringColor(() => (t: number) => `rgba(95, 214, 200, ${1 - t})`)
      .ringMaxRadius(4)
      .ringPropagationSpeed(2)
      .ringRepeatPeriod(1400)
      .ringAltitude(0.012)

    // Arcs between language hubs
    globe
      .arcsData(AFRICA_LANG_ARCS)
      .arcStartLat('startLat')
      .arcStartLng('startLng')
      .arcEndLat('endLat')
      .arcEndLng('endLng')
      .arcColor(() => [`rgba(95, 214, 200, 0.9)`, `rgba(117, 203, 203, 0.4)`])
      .arcAltitudeAutoScale(0.35)
      .arcStroke(0.45)
      .arcDashLength(0.4)
      .arcDashGap(0.5)
      .arcDashAnimateTime(2400)

    // Apply hex-dot polygons once the geojson loads
    fetch('/data/countries.geojson')
      .then(r => r.json())
      .then((data: { features: CountryFeature[] }) => {
        const features = data.features || []
        globe
          .hexPolygonsData(features)
          .hexPolygonResolution(3)
          .hexPolygonMargin(0.35)
          .hexPolygonUseDots(true)
          .hexPolygonColor((d: object) => {
            const f = d as CountryFeature
            const continent = f.properties?.CONTINENT
            if (continent === 'Africa') return ACCENT_LIGHT
            return themeRef.current
              ? 'rgba(160, 160, 180, 0.35)'
              : 'rgba(60, 75, 90, 0.45)'
          })
        setReady(true)
      })
      .catch(() => {
        // Even without geojson, rings/arcs still render
        setReady(true)
      })

    // Pointer drag rotation
    let isDragging = false
    let lastX = 0
    let lastY = 0
    let velX = 0
    let velY = 0
    let autoSpeed = prefersReducedMotion ? 0 : 0.0018

    const onPointerDown = (e: PointerEvent) => {
      isDragging = true
      lastX = e.clientX
      lastY = e.clientY
      renderer.domElement.style.cursor = 'grabbing'
      ;(e.target as Element).setPointerCapture?.(e.pointerId)
    }
    const onPointerMove = (e: PointerEvent) => {
      if (!isDragging) return
      const dx = e.clientX - lastX
      const dy = e.clientY - lastY
      velX = dx * 0.005
      velY = dy * 0.005
      globe.rotation.y += velX
      globe.rotation.x = Math.max(-1, Math.min(1, globe.rotation.x + velY))
      lastX = e.clientX
      lastY = e.clientY
    }
    const onPointerUp = (e: PointerEvent) => {
      isDragging = false
      renderer.domElement.style.cursor = 'grab'
      ;(e.target as Element).releasePointerCapture?.(e.pointerId)
    }

    renderer.domElement.style.cursor = 'grab'
    renderer.domElement.style.touchAction = 'none'
    renderer.domElement.addEventListener('pointerdown', onPointerDown)
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)

    // Animate
    let raf = 0
    const animate = () => {
      raf = requestAnimationFrame(animate)

      if (!isDragging) {
        // Apply momentum then decay
        if (Math.abs(velX) > 0.0001 || Math.abs(velY) > 0.0001) {
          globe.rotation.y += velX
          globe.rotation.x = Math.max(-1, Math.min(1, globe.rotation.x + velY))
          velX *= 0.93
          velY *= 0.93
        }
        // Auto-rotate
        globe.rotation.y += autoSpeed
      }

      renderer.render(scene, camera)
    }
    animate()

    // Pause auto-rotate on hover for accessibility
    const onEnter = () => { autoSpeed = 0 }
    const onLeave = () => { autoSpeed = prefersReducedMotion ? 0 : 0.0018 }
    renderer.domElement.addEventListener('pointerenter', onEnter)
    renderer.domElement.addEventListener('pointerleave', onLeave)

    // Resize
    const onResize = () => {
      const w = mount.clientWidth
      const h = mount.clientHeight
      camera.aspect = w / h
      camera.updateProjectionMatrix()
      renderer.setSize(w, h)
    }
    const ro = new ResizeObserver(onResize)
    ro.observe(mount)

    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
      renderer.domElement.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      renderer.domElement.removeEventListener('pointerenter', onEnter)
      renderer.domElement.removeEventListener('pointerleave', onLeave)
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement)
      }
      renderer.dispose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefersReducedMotion])

  if (!supported) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div
          className="w-64 h-64 rounded-full"
          style={{
            background: `radial-gradient(circle at 30% 30%, ${ACCENT_LIGHT}, ${ACCENT} 60%, transparent 75%)`,
            opacity: 0.4,
          }}
        />
      </div>
    )
  }

  return (
    <div className="relative w-full h-full">
      <div
        ref={mountRef}
        className="absolute inset-0"
        role="img"
        aria-label="Interactive 3D globe highlighting MATNO's language work across Africa. Drag to rotate."
        style={{
          opacity: ready ? 1 : 0,
          transition: 'opacity 700ms ease-out',
        }}
      />
      {/* Subtle radial glow behind globe */}
      <div
        className="absolute inset-0 pointer-events-none -z-10"
        style={{
          background: isDark
            ? `radial-gradient(circle at 50% 50%, ${ACCENT}33 0%, transparent 60%)`
            : `radial-gradient(circle at 50% 50%, ${ACCENT_BRIGHT}22 0%, transparent 60%)`,
        }}
      />
    </div>
  )
}

export default GlobeShowcase
